import { createStyles } from "@mantine/core";
import { QR, BTN_RESET } from "../../pages/Home/QuarterlyReportsPage/theme";

const useDrawerStyles = createStyles(() => ({
  wizSection: {
    padding: "18px 0 6px",
    borderTop: `1px solid ${QR.lineSoft}`,
    "&:first-of-type": { borderTop: "none" },
  },
  wizHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 },
  stepNum: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: QR.purple,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },
  wizLabel: { fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: QR.muted },
  wizHint: { marginLeft: "auto", fontSize: 11.5, fontWeight: 600, color: QR.muted },

  drawerX: {
    ...BTN_RESET,
    width: 34,
    height: 34,
    borderRadius: 8,
    color: QR.muted,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": { background: QR.surfaceAlt, color: QR.ink },
  },

  select: {
    ...BTN_RESET,
    width: "100%",
    height: 38,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "0 12px",
    background: QR.surface,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 13.5,
    color: QR.ink,
    fontFamily: "Inter",
    "&:hover": { borderColor: QR.accent },
  },
  input: {
    width: "100%",
    height: 38,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "0 12px",
    fontSize: 13.5,
    fontFamily: "Inter",
  },
  inputError: { borderColor: QR.danger },
  inputItalic: { fontStyle: "italic" },
  requiredMark: { color: QR.dangerDeep },

  // Signature modal (.modal-body / .modal-foot / .sig-preview)
  modalBody: { display: "flex", flexDirection: "column", gap: 14 },
  modalFoot: { borderTop: `1px solid ${QR.lineSoft}` },
  btnInner: { display: "flex", alignItems: "center", gap: 8 },
  sigPreviewImage: { maxHeight: 70, maxWidth: "100%", objectFit: "contain" },
  sigPreviewScript: {
    fontFamily: '"Segoe Script","Snell Roundhand","Brush Script MT",cursive',
    fontSize: 22,
  },

  ddPop: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 10,
    boxShadow: "0 12px 32px rgba(15,23,42,.16)",
    padding: 6,
    minWidth: 260,
  },
  ddOpt: {
    ...BTN_RESET,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "9px 12px",
    borderRadius: 7,
    fontSize: 13.5,
    fontWeight: 500,
    color: QR.ink2,
    fontFamily: "Inter",
    "&:hover": { background: QR.surfaceSoft },
  },
  ddOptSel: { fontWeight: 700, color: QR.ink },
  ddMeta: { fontSize: 12, color: QR.muted, fontWeight: 400 },

  clientList: { border: `1px solid ${QR.line}`, borderRadius: 12, overflow: "hidden" },
  clientListHead: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "11px 14px",
    background: QR.surfaceSoft,
    borderBottom: `1px solid ${QR.lineSoft}`,
    fontSize: 13,
    fontWeight: 600,
    color: QR.ink2,
    fontFamily: "Inter",
  },
  clientListHeadCollapsed: { borderBottomColor: "transparent" },
  clientCount: { marginLeft: "auto", fontSize: 12, color: QR.muted, fontVariantNumeric: "tabular-nums" },
  clientCollapseBtn: {
    ...BTN_RESET,
    width: 26,
    height: 26,
    borderRadius: 7,
    display: "grid",
    placeItems: "center",
    color: QR.muted,
    border: "1px solid transparent",
    transition: "transform .2s ease, background .15s",
    "&:hover": { background: QR.surfaceAlt, color: QR.ink2 },
  },
  clientCollapseBtnRotated: { transform: "rotate(-90deg)" },
  clientRows: { maxHeight: 544, overflowY: "auto", transition: "max-height .24s ease" },
  clientRowsCollapsed: { maxHeight: 0, overflow: "hidden" },
  clientRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "11px 14px",
    borderTop: `1px solid ${QR.lineSoft}`,
    "&:first-of-type": { borderTop: "none" },
    "&:hover": { background: QR.surfaceSoft },
  },
  clientRowOff: { opacity: 0.55 },
  clientGrow: { flex: 1, minWidth: 0 },
  clientAv: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: QR.surfaceAlt,
    color: QR.ink2,
    fontSize: 12,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },
  clientName: { fontSize: 13.5, fontWeight: 600, color: QR.ink, fontFamily: "Inter" },
  warnPill: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#92400E",
    background: QR.accentSoft,
    border: `1px solid ${QR.accentRing}`,
    borderRadius: 999,
    padding: "2px 8px",
    marginTop: 3,
    display: "inline-block",
  },
  cbx: {
    width: 18,
    height: 18,
    border: `1.5px solid ${QR.muted2}`,
    borderRadius: 5,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: QR.surface,
    cursor: "pointer",
  },
  cbxOn: {
    background: QR.purple,
    borderColor: QR.purple,
    "&::after": {
      content: '""',
      width: 9,
      height: 5,
      borderLeft: "2px solid #fff",
      borderBottom: "2px solid #fff",
      transform: "rotate(-45deg) translate(0,-1px)",
    },
  },
  noteChip: {
    fontSize: 11.5,
    fontWeight: 600,
    color: QR.muted,
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 999,
    padding: "3px 10px",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  noteChipZero: { color: "#B91C1C", background: QR.dangerSoft, borderColor: "#FECACA" },

  // range picker
  rangePop: {
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 14,
    boxShadow: "0 -12px 40px rgba(15,23,42,.18)",
    display: "flex",
    overflow: "hidden",
  },
  rpPresets: {
    width: 180,
    flexShrink: 0,
    borderRight: `1px solid ${QR.lineSoft}`,
    background: QR.surfaceSoft,
    padding: 10,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  rpPresetsLbl: { fontSize: 10.5, fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", color: QR.muted2, padding: "4px 10px 6px" },
  rpPreset: {
    ...BTN_RESET,
    padding: "9px 12px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: QR.ink2,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    border: "1px solid transparent",
    "&:hover": { background: QR.surfaceAlt },
  },
  rpPresetOn: { background: QR.purpleSoft, color: QR.purpleDeep, borderColor: QR.purpleRing },
  rpPresetSub: { fontSize: 11, fontWeight: 500, color: QR.muted, fontVariantNumeric: "tabular-nums" },
  rpMain: { display: "flex", flexDirection: "column" },
  rpCal: { padding: "14px 16px 12px", width: 280 },
  rpFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "11px 16px",
    borderTop: `1px solid ${QR.lineSoft}`,
  },
  rpRangeText: { fontSize: 12.5, fontWeight: 600, color: QR.ink2, fontVariantNumeric: "tabular-nums" },
  rpApply: {
    ...BTN_RESET,
    height: 32,
    padding: "0 16px",
    borderRadius: 8,
    background: QR.purple,
    color: "#fff",
    fontSize: 12.5,
    fontWeight: 700,
    textAlign: "center",
    "&:hover": { background: QR.purpleDeep },
  },
  calHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  calTitle: { fontSize: 13, fontWeight: 700, color: QR.ink },
  calNav: {
    ...BTN_RESET,
    width: 28,
    height: 28,
    borderRadius: 7,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: QR.muted,
    border: `1px solid ${QR.line}`,
    background: QR.surface,
    "&:hover": { background: QR.surfaceSoft },
  },
  calGrid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 },
  calDow: { fontSize: 10.5, fontWeight: 700, color: QR.muted2, textAlign: "center", padding: "4px 0", textTransform: "uppercase" },
  calDay: {
    height: 30,
    borderRadius: 7,
    fontSize: 12.5,
    fontWeight: 500,
    color: QR.ink2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontVariantNumeric: "tabular-nums",
    "&:hover": { background: QR.surfaceAlt },
  },
  calDayMuted: { color: "#D4D4D8", cursor: "default", pointerEvents: "none" },
  calDayInRange: { background: QR.purpleSoft },
  calDayEdge: { background: QR.purple, color: "#fff", fontWeight: 700 },

  // drawer foot / ghost buttons
  btnGhost: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: `1px solid ${QR.line}`,
    background: QR.surface,
    fontWeight: 600,
    fontSize: 13.5,
    color: QR.ink2,
    fontFamily: "Inter",
    "&:hover": { background: QR.surfaceSoft },
  },
  btnAdd: {
    ...BTN_RESET,
    height: 40,
    padding: "0 18px",
    borderRadius: 8,
    background: QR.accent,
    color: QR.ink,
    fontWeight: 700,
    fontSize: 13.5,
    fontFamily: "Inter",
    textAlign: "center",
    "&:hover": { background: QR.accentHover },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },

  // progress pane
  progSummary: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: QR.surfaceSoft,
    border: `1px solid ${QR.lineSoft}`,
    borderRadius: 14,
    padding: "16px 18px",
    marginBottom: 18,
  },
  progSummaryTitle: { fontSize: 15, fontWeight: 700, color: QR.ink, fontFamily: "Inter" },
  progSummaryMeta: { fontSize: 12.5, color: QR.muted, marginTop: 2 },
  progRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    padding: "12px 14px",
    marginBottom: 8,
    background: QR.surface,
  },
  progRowClickable: { cursor: "pointer", "&:hover": { background: QR.surfaceSoft } },
  progAv: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    background: QR.surfaceAlt,
    color: QR.ink2,
    fontSize: 12,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },
  statusChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 11px",
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 700,
    border: "1px solid",
    whiteSpace: "nowrap",
    fontFamily: "Inter",
  },
  errMeta: { fontSize: 12, color: QR.dangerDeep, marginTop: 3 },
  // .row-btn.danger in the prototype — a bordered, danger-soft-filled pill,
  // not a plain hover-only link. Was missing its own resting
  // background/border entirely (only appeared on hover) and lacked
  // BTN_RESET, which is why it never looked like the mockup's button.
  rowBtnDanger: {
    ...BTN_RESET,
    fontSize: 12.5,
    fontWeight: 600,
    color: QR.dangerDeep,
    background: QR.dangerSoft,
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "6px 14px",
    textAlign: "center",
    "&:hover": { background: "#FEE2E2" },
  },
  spinner: {
    width: 13,
    height: 13,
    borderRadius: "50%",
    border: `2px solid ${QR.accentRing}`,
    borderTopColor: QR.accentHover,
    animation: "qr-spin 1s linear infinite",
  },
  "@global": {
    "@keyframes qr-spin": { to: { transform: "rotate(360deg)" } },
  },

  // ---- progress ring (setup/progress pane header ring) ----
  progRingWrap: { position: "relative", width: 62, height: 62, flexShrink: 0 },
  progRingSvg: { transform: "rotate(-90deg)" },
  progRingCircleFill: { transition: "stroke-dashoffset .5s ease" },
  progRingLabel: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    color: QR.ink,
  },
  progRingDone: { fontSize: 18 },
  progRingTotal: { fontSize: 12, fontWeight: 700, color: QR.muted2 },

  // ---- drawer pane chrome (setup / progress panes share this shell) ----
  // flex: 1 + minHeight: 0, not height: 100% — Drawer.Body (its direct
  // parent, see GenerateDrawer's styles={{ body: { display: "flex",
  // flexDirection: "column", flex: 1, minHeight: 0 } }}) is itself a flex
  // column, so this just needs to be a normal flex item within it. minHeight:
  // 0 is required — without it a flex column child defaults to min-height:
  // auto (its content's intrinsic height), so this column just grows taller
  // than the Drawer instead of ever bounding paneBody to an actually
  // scrollable height.
  paneCol: { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 },
  paneHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "22px 28px",
    borderBottom: `1px solid ${QR.lineSoft}`,
  },
  paneBody: { flex: 1, overflowY: "auto", padding: "8px 28px 24px" },
  paneFoot: {
    padding: "16px 28px",
    borderTop: `1px solid ${QR.line}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  progNameWrap: { flex: 1, minWidth: 0 },
  retryBtnInner: { display: "flex", alignItems: "center", gap: 6 },

  // ---- status chip variants (per-status colors, no inline style needed) ----
  statusChipReady: { background: QR.successSoft, color: QR.successDeep, borderColor: "#BBF7D0" },
  statusChipGenerating: { background: QR.accentSoft, color: "#B45309", borderColor: QR.accentRing },
  statusChipWaiting: { background: QR.surfaceAlt, color: QR.muted2, borderColor: QR.line },
  statusChipFailed: { background: QR.dangerSoft, color: QR.dangerDeep, borderColor: "#FECACA" },
}));

export default useDrawerStyles;
